import { useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { RenderReport } from '@inference.engine/widget-html/web';
import {
  uploadWidgetPreview,
  useWidgetDefinition,
  widgetQueryKeys,
  type WidgetDefinition,
} from '@/entities/widget-definition';
import { pngBlobOf } from '../../lib/data-url';
import { HtmlWidgetFrame } from '../HtmlWidgetFrame/HtmlWidgetFrame';
import { previewWidgetOf, PREVIEW_WIDTHS, THEMES, type PreviewWidth } from './preview-widget';

export type ShotReports = Partial<Record<PreviewWidth, RenderReport>>;

/**
 * Draws the draft off screen at each of its placement's widths, light and dark, and uploads what each
 * frame drew, image and render report, once per draft revision: what the Widget agent's
 * `get_screenshot` waits for. A capture that fails still sends its report.
 */
export function PreviewCapture({
  widget,
  onShot,
}: {
  widget: WidgetDefinition;
  onShot?: (width: PreviewWidth, report: RenderReport) => void;
}) {
  const draft = widget.draft;
  if (!draft?.document) return null;
  return (
    <div aria-hidden className="pointer-events-none fixed top-0 -left-[10000px]">
      {PREVIEW_WIDTHS[draft.placement].flatMap((width) =>
        THEMES.map((theme) => (
          <Shot
            key={`${widget.draftRevision}-${width}-${theme}`}
            widget={widget}
            width={width}
            theme={theme}
            onShot={onShot}
          />
        )),
      )}
    </div>
  );
}

/**
 * `PreviewCapture` for a widget known only by its id, as the canvas holds it, so the Widget agent can
 * look at its work while the person designs it from the canvas's Flow.Assist.
 */
export function WidgetCapture({ widgetId }: { widgetId: string }) {
  const { data: widget } = useWidgetDefinition(widgetId);
  return widget ? <PreviewCapture widget={widget} /> : null;
}

function Shot({
  widget,
  width,
  theme,
  onShot,
}: {
  widget: WidgetDefinition;
  width: PreviewWidth;
  theme: 'light' | 'dark';
  onShot: ((width: PreviewWidth, report: RenderReport) => void) | undefined;
}) {
  const queryClient = useQueryClient();
  const report = useRef<RenderReport | undefined>(undefined);
  const capture = useRef<(() => Promise<string>) | undefined>(undefined);
  const sent = useRef(false);
  // The server's thumbnail is the light preview at the placement's narrowest width.
  const isThumbnail =
    theme === 'light' && width === PREVIEW_WIDTHS[widget.draft?.placement ?? widget.placement][0];

  const send = useCallback(async () => {
    if (sent.current || !report.current || !capture.current) return;
    sent.current = true;
    const measured = report.current;
    if (theme === 'light') onShot?.(width, measured);
    let image: Blob | undefined;
    try {
      image = pngBlobOf(await capture.current());
    } catch {
      image = undefined;
    }
    const uploaded = await uploadWidgetPreview(widget.id, {
      draftRevision: widget.draftRevision,
      width,
      theme,
      report: measured,
      ...(image ? { image } : {}),
    }).then(
      () => true,
      () => false,
    );
    if (uploaded && image && isThumbnail) {
      void queryClient.invalidateQueries({ queryKey: widgetQueryKeys.thumbnail(widget.id) });
    }
  }, [widget.id, widget.draftRevision, width, theme, onShot, isThumbnail, queryClient]);

  return (
    <div style={{ width }}>
      <HtmlWidgetFrame
        widget={previewWidgetOf(widget, 0)}
        theme={theme}
        onReport={(next) => {
          report.current = next;
          void send();
        }}
        onCaptureReady={(next) => {
          capture.current = next;
          void send();
        }}
      />
    </div>
  );
}
